import {
  AlertCircle,
  Download,
  Eye,
  FileText,
  Loader2,
  RefreshCw,
} from "lucide-react";
import React, { useEffect, useState } from "react";

import { useAppSelector } from "@/hooks/redux";
import documentsService, {
  Document,
  RequiredDocument,
} from "@/services/documentsService";

import DeliveryDriverCard from "./cards/DeliveryDriverCard";
import ScooterDriverCard from "./cards/ScooterDriverCard";
import VehicleDriverCard from "./cards/VehicleDriverCard";

// Document interface moved to documentsService.ts

interface DocumentsProps {
  // Add props here if needed
}

const Documents: React.FC<DocumentsProps> = () => {
  const { user } = useAppSelector((state) => state.auth);
  const [uploadedDocuments, setUploadedDocuments] = useState<{
    [key: string]: boolean;
  }>({});
  const [isUploading, setIsUploading] = useState(false);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [requiredDocuments, setRequiredDocuments] = useState<
    RequiredDocument[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<null | string>(null);

  // Load documents and required documents on component mount
  useEffect(() => {
    loadDocuments();
    loadRequiredDocuments();
  }, []);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      setError(null); // Clear any previous errors

      const response = await documentsService.getUserDocuments();

      // Check if response has documents array (backend returns documents directly)
      if (response.documents && Array.isArray(response.documents)) {
        setDocuments(response.documents);

        // Update uploadedDocuments state based on existing documents
        const uploadedState: { [key: string]: boolean } = {};
        response.documents.forEach((doc) => {
          // Map backend document_type back to frontend type for UI state
          const backendType = doc.document_type || doc.type;
          const filename = doc.original_filename || doc.name;
          const frontendType = documentsService.mapBackendToFrontendType(
            backendType,
            doc.description,
            filename,
          );

          uploadedState[frontendType] = true;
        });

        setUploadedDocuments(uploadedState);
      } else {
        // Still clear the states even if no documents
        setDocuments([]);
        setUploadedDocuments({});
      }
    } catch (err) {
      console.error("Failed to load documents:", err);
      setError("Failed to load documents. Please try again.");
      // Reset states on error
      setDocuments([]);
      setUploadedDocuments({});
    } finally {
      setLoading(false);
    }
  };

  const loadRequiredDocuments = async () => {
    try {
      const response = await documentsService.getRequiredDocuments();
      // Check if response has required_documents (adapt to actual backend response)
      if (
        response.required_documents &&
        Array.isArray(response.required_documents)
      ) {
        setRequiredDocuments(response.required_documents);
      } else if (response.success && response.required_documents) {
        // Fallback for if backend includes success property
        setRequiredDocuments(response.required_documents);
      }
    } catch (err) {
      console.error("Failed to load required documents:", err);
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Handle file upload logic here
    }
  };

  const handleDocumentUpload = async (documentType: string, file: File) => {
    setIsUploading(true);
    try {
      const response = await documentsService.uploadDocument(
        documentType,
        file,
      );

      if (response.document) {
        // Create a normalized document object for the frontend
        const normalizedDoc: Document = {
          admin_notes: false,
          description: response.document.description,
          document_type: response.document.document_type,
          file_size: response.document.file_size,
          id: response.document.id,
          name: response.document.original_filename,
          original_filename: response.document.original_filename,
          status: response.document.status,
          type: documentType,
          uploadDate: response.document.uploaded_at,
          uploaded_at: response.document.uploaded_at,
        };

        // Update uploaded documents state
        setUploadedDocuments((prev) => ({
          ...prev,
          [documentType]: true,
        }));

        // Add new document to the list
        setDocuments((prev) => [...prev, normalizedDoc]);

        // Refresh documents list to ensure consistency
        setTimeout(() => {
          loadDocuments();
        }, 1000);
      } else {
        throw new Error(response.message || "Upload failed");
      }
    } catch (error) {
      console.error("Upload failed:", error);
      setError(`Failed to upload ${documentType}. Please try again.`);
    } finally {
      setIsUploading(false);
    }
  };

  const handleView = async (document: Document) => {
    try {
      const blob = await documentsService.downloadDocument(document.id);
      const url = window.URL.createObjectURL(blob);

      // Open in new tab/window for viewing
      const newWindow = window.open(url, "_blank");
      if (newWindow) {
        // Clean up the URL after the window is loaded
        newWindow.onload = () => {
          setTimeout(() => {
            window.URL.revokeObjectURL(url);
          }, 1000);
        };
      } else {
        // Fallback if popup is blocked - just revoke URL
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
        }, 1000);
      }
    } catch (error) {
      console.error("View failed:", error);
      setError("Failed to view document. Please try again.");
    }
  };

  const handleDownload = async (document: Document) => {
    try {
      const blob = await documentsService.downloadDocument(document.id);
      const filename =
        document.original_filename || document.name || "document";
      documentsService.downloadFile(blob, filename);
    } catch (error) {
      console.error("Download failed:", error);
      setError("Failed to download document. Please try again.");
    }
  };

  const handleReplace = async (document: Document) => {
    // Create a file input to select new file
    const input = window.document.createElement("input");
    input.type = "file";
    input.accept = ".pdf,.jpg,.jpeg,.png,.doc,.docx";
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        setIsUploading(true);
        try {
          const response = await documentsService.replaceDocument(
            document.id,
            file,
            document.description,
          );

          if (response.document) {
            // Update the document in the list
            setDocuments((prev) =>
              prev.map((doc) =>
                doc.id === document.id
                  ? {
                      ...doc,
                      description:
                        response.document!.description || doc.description,
                      file_size: response.document!.file_size,
                      original_filename: response.document!.original_filename,
                      status: response.document!.status,
                      uploaded_at: response.document!.uploaded_at,
                    }
                  : doc,
              ),
            );

            // Refresh documents list to ensure consistency
            setTimeout(() => {
              loadDocuments();
            }, 1000);
          } else {
            throw new Error(response.message || "Replace failed");
          }
        } catch (error) {
          console.error("Replace failed:", error);
          setError("Failed to replace document. Please try again.");
        } finally {
          setIsUploading(false);
        }
      }
    };
    input.click();
  };

  const handleDelete = async (documentId: string) => {
    if (!confirm("Are you sure you want to delete this document?")) return;

    try {
      const response = await documentsService.deleteDocument(documentId);
      // Check for success in response (adapt to actual backend response)
      if (response.success || response.message) {
        setDocuments((prev) => prev.filter((doc) => doc.id !== documentId));
        // Update uploaded documents state
        const deletedDoc = documents.find((doc) => doc.id === documentId);
        if (deletedDoc) {
          const docType = deletedDoc.document_type || deletedDoc.type;
          const filename = deletedDoc.original_filename || deletedDoc.name;
          const frontendType = documentsService.mapBackendToFrontendType(
            docType,
            deletedDoc.description,
            filename,
          );
          setUploadedDocuments((prev) => ({
            ...prev,
            [frontendType]: false,
          }));
        }
      }
    } catch (error) {
      console.error("Delete failed:", error);
      setError("Failed to delete document. Please try again.");
    }
  };

  const renderDriverDocumentCard = () => {
    if (!user?.role) return null;

    switch (user.role) {
      case "DeliveryDriver":
        return (
          <DeliveryDriverCard
            isUploading={isUploading}
            onFileUpload={handleDocumentUpload}
            uploadedDocuments={uploadedDocuments}
          />
        );
      case "ScooterDriver":
        return (
          <ScooterDriverCard
            isUploading={isUploading}
            onFileUpload={handleDocumentUpload}
            uploadedDocuments={uploadedDocuments}
          />
        );
      case "VehicleDriver":
        return (
          <VehicleDriverCard
            isUploading={isUploading}
            onFileUpload={handleDocumentUpload}
            uploadedDocuments={uploadedDocuments}
          />
        );
      default:
        return (
          <div className="rounded-lg bg-white p-6 text-center shadow-lg dark:bg-gray-800">
            <p className="text-gray-600 dark:text-gray-400">
              Please complete your profile to see document requirements.
            </p>
          </div>
        );
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4 dark:bg-gray-900 sm:p-6 lg:p-8">
        <div className="text-center">
          <Loader2 className="mx-auto mb-4 h-8 w-8 animate-spin text-blue-600" />
          <p className="text-gray-600 dark:text-gray-400">
            Loading documents...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 dark:bg-gray-900 sm:p-6 lg:p-8">
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="mb-2 text-2xl font-bold text-gray-900 dark:text-gray-100 sm:text-3xl lg:text-4xl">
              Documents
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400 sm:text-base">
              Manage your driver documents and certifications.
            </p>
          </div>
          <div className="mt-4 sm:mt-0">
            <div className="flex space-x-2">
              <button
                className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                disabled={loading}
                onClick={() => loadDocuments()}
              >
                <RefreshCw
                  className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`}
                />
                Refresh
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 rounded-lg bg-red-50 p-3 dark:bg-red-900/20 sm:mb-6 sm:p-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-0">
            <div className="flex items-center">
              <AlertCircle className="mr-2 h-4 w-4 flex-shrink-0 text-red-500 sm:h-5 sm:w-5" />
              <p className="text-xs text-red-700 dark:text-red-400 sm:text-sm">
                {error}
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <button
                className="text-xs text-red-600 underline hover:text-red-800 dark:text-red-400 dark:hover:text-red-200 sm:text-sm"
                onClick={() => loadDocuments()}
              >
                Retry
              </button>
              <button
                className="text-base font-semibold text-red-500 hover:text-red-700 sm:text-lg"
                onClick={() => setError(null)}
              >
                ×
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Role-based Document Upload Cards */}
      <div className="mb-6 sm:mb-8">{renderDriverDocumentCard()}</div>

      {/* Documents List */}
      <div className="rounded-lg bg-white shadow-md dark:bg-gray-800">
        <div className="border-b border-gray-200 p-4 dark:border-gray-700 sm:p-5 lg:p-6">
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 sm:text-lg lg:text-xl">
            Your Documents
          </h2>
        </div>
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {documents.map((doc) => (
            <div
              className="flex flex-col space-y-2.5 p-3 sm:space-y-3 sm:p-4 lg:flex-row lg:items-center lg:justify-between lg:space-y-0 lg:p-6"
              key={doc.id}
            >
              <div className="flex items-start sm:items-center">
                <div className="mr-2.5 flex-shrink-0 rounded-full bg-blue-100 p-1.5 text-blue-600 dark:bg-blue-900 dark:text-blue-400 sm:mr-3 sm:p-2 lg:mr-4 lg:p-3">
                  <FileText className="h-4 w-4 sm:h-5 sm:w-5 lg:h-6 lg:w-6" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="truncate text-sm font-medium text-gray-900 dark:text-gray-100 sm:text-base lg:text-lg">
                    {doc.original_filename || doc.name}
                  </h3>
                  <div className="mt-0.5 flex flex-col space-y-0.5 text-xs text-gray-500 dark:text-gray-400 sm:mt-1 sm:flex-row sm:items-center sm:space-y-0">
                    <span>
                      Uploaded:{" "}
                      {new Date(
                        doc.uploaded_at || doc.uploadDate,
                      ).toLocaleDateString()}
                    </span>
                    {doc.expiryDate && (
                      <>
                        <span className="mx-2 hidden sm:inline">•</span>
                        <span>
                          Expires:{" "}
                          {new Date(doc.expiryDate).toLocaleDateString()}
                        </span>
                      </>
                    )}
                  </div>
                  {doc.description && (
                    <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400 sm:mt-1">
                      {doc.description}
                    </p>
                  )}
                  {doc.admin_notes &&
                    (doc.status === "rejected" ||
                      doc.status === "request-resubmission") && (
                      <div className="mt-1 rounded bg-red-50 p-2 text-xs text-red-700 dark:bg-red-900/20 dark:text-red-300">
                        <strong>Admin message:</strong> {doc.admin_notes}
                      </div>
                    )}
                </div>
              </div>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between lg:justify-end lg:space-x-4">
                <span
                  className={`inline-flex w-fit items-center rounded-full px-2 py-0.5 text-xs font-medium capitalize sm:px-2.5 ${documentsService.getStatusColor(doc.status)}`}
                >
                  {doc.status}
                </span>
                <div className="flex flex-wrap gap-1.5 sm:gap-2">
                  <button
                    className="flex items-center gap-1 rounded bg-purple-50 px-2 py-1 text-xs font-medium text-purple-600 hover:text-purple-900 dark:bg-purple-900/20 dark:text-purple-400 dark:hover:text-purple-300 lg:rounded-none lg:bg-transparent lg:px-0 lg:py-0 lg:text-sm lg:dark:bg-transparent"
                    onClick={() => handleView(doc)}
                  >
                    <Eye className="h-3 w-3 lg:hidden" />
                    <span className="lg:inline">View</span>
                  </button>
                  <button
                    className="flex items-center gap-1 rounded bg-blue-50 px-2 py-1 text-xs font-medium text-blue-600 hover:text-blue-900 dark:bg-blue-900/20 dark:text-blue-400 dark:hover:text-blue-300 lg:rounded-none lg:bg-transparent lg:px-0 lg:py-0 lg:text-sm lg:dark:bg-transparent"
                    onClick={() => handleDownload(doc)}
                  >
                    <Download className="h-3 w-3 lg:hidden" />
                    <span className="lg:inline">Download</span>
                  </button>
                  <button
                    className={`rounded px-2.5 py-1.5 text-xs font-medium sm:rounded-none sm:bg-transparent sm:px-0 sm:py-0 sm:text-sm sm:dark:bg-transparent ${
                      doc.status === "pending" ||
                      doc.status === "request-resubmission"
                        ? "cursor-pointer bg-gray-50 text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:bg-gray-700/50 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-300 sm:hover:bg-transparent sm:dark:hover:bg-transparent"
                        : "cursor-not-allowed bg-gray-100 text-gray-400 opacity-50 dark:bg-gray-800 dark:text-gray-600"
                    }`}
                    disabled={
                      doc.status !== "pending" &&
                      doc.status !== "request-resubmission"
                    }
                    onClick={async () => {
                      if (
                        doc.status !== "pending" &&
                        doc.status !== "request-resubmission"
                      )
                        return;

                      const input = window.document.createElement("input");
                      input.type = "file";
                      input.accept = ".pdf,.jpg,.jpeg,.png,.doc,.docx";
                      input.onchange = async (e) => {
                        const file = (e.target as HTMLInputElement).files?.[0];
                        if (file) {
                          setIsUploading(true);
                          try {
                            const response =
                              await documentsService.replaceDocument(
                                doc.id,
                                file,
                                doc.description,
                              );
                            if (response.document) {
                              setDocuments((prev) =>
                                prev.map((d) =>
                                  d.id === doc.id
                                    ? { ...d, ...response.document }
                                    : d,
                                ),
                              );
                            }
                          } catch (error) {
                            setError(
                              "Failed to replace document. Please try again.",
                            );
                          } finally {
                            setIsUploading(false);
                          }
                        }
                      };
                      input.click();
                    }}
                    title={
                      doc.status === "pending" ||
                      doc.status === "request-resubmission"
                        ? "Replace document"
                        : "Replace is only available for pending or resubmission documents"
                    }
                  >
                    Replace
                  </button>
                  <button
                    className={`rounded bg-red-50 px-2 py-1 text-xs font-medium dark:bg-red-900/20 lg:rounded-none lg:bg-transparent lg:px-0 lg:py-0 lg:text-sm lg:dark:bg-transparent ${
                      doc.status === "pending"
                        ? "cursor-pointer text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                        : "cursor-not-allowed text-red-400 opacity-50 dark:text-red-600"
                    }`}
                    disabled={doc.status !== "pending"}
                    onClick={() => handleDelete(doc.id)}
                    title={
                      doc.status !== "pending"
                        ? "Delete is only available for pending documents"
                        : "Delete document"
                    }
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Dynamic Requirements Section */}
      {requiredDocuments.length > 0 && (
        <div className="mt-4 rounded-lg bg-blue-50 p-3 dark:bg-blue-900/20 sm:mt-6 sm:p-4 lg:mt-8 lg:p-6">
          <h3 className="mb-2 text-sm font-semibold text-blue-900 dark:text-blue-400 sm:mb-3 sm:text-base lg:text-lg">
            Required Documents for {user?.role || "Driver"}
          </h3>
          <ul className="space-y-0.5 text-xs text-blue-800 dark:text-blue-300 sm:space-y-1 sm:text-sm">
            {requiredDocuments.map((reqDoc) => (
              <li key={reqDoc.type}>
                • <strong>{reqDoc.name}</strong>
                {reqDoc.required && (
                  <span className="ml-1 text-red-500">*</span>
                )}
                {reqDoc.description && (
                  <span className="ml-2 text-blue-600 dark:text-blue-400">
                    - {reqDoc.description}
                  </span>
                )}
              </li>
            ))}
          </ul>
          <div className="mt-3 space-y-0.5 text-xs text-blue-600 dark:text-blue-400 sm:mt-4">
            <p>• All documents must be clear and readable</p>
            <p>• Documents should not be expired</p>
            <p>• File formats accepted: PDF, JPG, PNG</p>
            <p>• Maximum file size: 10MB</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Documents;
